/**
 * Đăng bài và Ảnh lên website thepminhtien.com (DEC-640, anh Tiến 31/10/2026):
 *  - NV văn phòng: chọn ảnh + gõ từ khóa → AI viết bài → NV kiểm, sửa → Đăng (mục Tin tức).
 *  - Kho, tài xế: "Hàng xuất hôm nay" → chọn ảnh xuất kho / giao hàng → Đăng (trang Hình ảnh thực tế).
 *  - Bài nào cũng ký tên "<Tên NV> – Minh Tiến 0933160169".
 * Web lấy bài đã đăng qua API có chữ ký HMAC (khóa của DEC-593, scope posts:read). Ảnh công khai không được lộ giá, tên khách, biển số.
 */
export const WEB_POST_KINDS = ['news', 'gallery'] as const;
export type WebPostKind = (typeof WEB_POST_KINDS)[number];
export const WEB_POST_KIND_LABELS: Record<WebPostKind, string> = { news: 'Bài viết (Tin tức)', gallery: 'Ảnh thực tế' };

/** Các mục trên trang "Hình ảnh thực tế" của website (đúng thứ tự nút lọc trên web). */
export const WEB_POST_SECTIONS = ['kho_xuong', 'nhap_khau', 'san_xuat', 'thi_cong', 'giao_hang'] as const;
export type WebPostSection = (typeof WEB_POST_SECTIONS)[number];
export const WEB_POST_SECTION_LABELS: Record<WebPostSection, string> = {
  kho_xuong: 'Kho – Xưởng',
  nhap_khau: 'Nhập khẩu',
  san_xuat: 'Sản xuất',
  thi_cong: 'Thi công',
  giao_hang: 'Giao hàng',
};

/** Ảnh chụp thật hay hình minh họa dựng / chỉnh bằng AI (web hiện nhãn khác nhau, như trang Hình ảnh thực tế). */
export const WEB_POST_IMAGE_TYPES = ['real', 'ai'] as const;
export type WebPostImageType = (typeof WEB_POST_IMAGE_TYPES)[number];
export const WEB_POST_IMAGE_TYPE_LABELS: Record<WebPostImageType, string> = { real: 'Ảnh thực tế', ai: 'Hình minh họa AI' };

/** Website nhận bài (anh Tiến 31/10/2026). pccc = web tĩnh trong repo (apps/site), đọc bài không cần ký vì không giữ được khóa bí mật. */
export const WEB_POST_TARGETS = ['thepminhtien', 'sieuthisatthep', 'pccc'] as const;
export type WebPostTarget = (typeof WEB_POST_TARGETS)[number];
export const WEB_POST_TARGET_LABELS: Record<WebPostTarget, string> = {
  thepminhtien: 'thepminhtien.com',
  sieuthisatthep: 'sieuthisatthep.net',
  pccc: 'pccc.sieuthisatthep.net',
};

/** Web WordPress: NV chọn được chuyên mục (Tin tức, Tin chuyên ngành…) ngay trên ERP (anh Tiến 01/11/2026, DEC-700). pccc là web tĩnh, không có chuyên mục. */
export const WEB_POST_WP_TARGETS = ['thepminhtien', 'sieuthisatthep'] as const;
export type WebPostWpTarget = (typeof WEB_POST_WP_TARGETS)[number];
export interface WebPostWpCategory {
  slug: string;
  name: string;
}
/** Chuyên mục đã chọn theo web; web không có trong đây = chuyên mục mặc định đặt ở plugin. */
export type WebPostWpCategories = Partial<Record<WebPostWpTarget, WebPostWpCategory>>;
/** Đường dẫn chuyên mục WordPress (slug): chữ thường, số, gạch ngang; tiếng Việt có dấu WordPress mã hóa %xx. */
export const WP_CATEGORY_SLUG_RE = /^[a-z0-9%_-]{1,200}$/;

/**
 * DEC-781 (anh Tiến 02/11/2026, tài liệu "ERP – thêm ô Vị trí đăng", plugin web 1.1.x): danh mục sản phẩm WooCommerce gắn với bài.
 * Ảnh hiện cuối trang danh mục sản phẩm đó trên web (cả danh mục cha); slug không có trên web thì web bỏ qua.
 */
export type WebPostWpProductCats = Partial<Record<WebPostWpTarget, WebPostWpCategory[]>>;
export const WEB_POST_MAX_PRODUCT_CATS = 10;
/** Web gọi đồng bộ lại tối đa 1 lần / 20 giây (plugin trả 429 nếu sớm hơn) */
export const WEB_POST_SYNC_MIN_GAP_MS = 20_000;

/**
 * SEO (anh Tiến 01/11/2026, DEC-700): thepminhtien.com và sieuthisatthep.net là 2 web chính. Một bài viết chỉ để Google
 * index trên 1 web, web khác gắn noindex; web index chạy xen kẽ theo ngày: 01/11/2026 thepminhtien.com, 02/11 sieuthisatthep.net…
 * `dateVN` dạng YYYY-MM-DD (giờ VN).
 */
export function webPostIndexTargetFor(dateVN: string): WebPostWpTarget {
  const [y, m, d] = dateVN.split('-').map(Number);
  const day = Math.round(Date.UTC(y!, m! - 1, d!) / 86_400_000);
  const anchor = Math.round(Date.UTC(2026, 10, 1) / 86_400_000);
  return (((day - anchor) % 2) + 2) % 2 === 0 ? 'thepminhtien' : 'sieuthisatthep';
}

/** pccc.sieuthisatthep.net là web phụ quảng cáo (anh Tiến 01/11/2026): bài viết mặc định không đăng, tối đa 2 bài viết / tuần (thứ Hai – Chủ nhật). */
export const WEB_POST_PCCC_NEWS_PER_WEEK = 2;
/**
 * Anh Tiến 02/11/2026: "dừng đăng trên web pccc.sieuthisatthep.net, trả về hiện tại lúc đầu". Không NV nào chọn được web PCCC;
 * bài cũ đã gỡ khỏi web PCCC (migration 0289). Mở lại: bỏ web khỏi danh sách này (giới hạn 2 bài viết / tuần vẫn giữ trong code).
 */
export const WEB_POST_PAUSED_TARGETS: readonly WebPostTarget[] = ['pccc'];
/** Web NV còn chọn được */
export const WEB_POST_ACTIVE_TARGETS: WebPostTarget[] = WEB_POST_TARGETS.filter((t) => !WEB_POST_PAUSED_TARGETS.includes(t));
/** Web tick sẵn khi soạn bài: bài viết lên 2 web chính, ảnh thực tế lên cả 3 web. */
/** Web tick sẵn: bài viết và ảnh đều lên 2 web chính; pccc.sieuthisatthep.net chỉ khi NV tick (DEC-780: anh Tiến 02/11/2026 tạm chưa đăng ảnh lên web PCCC). */
export function webPostDefaultTargets(_kind: WebPostKind): WebPostTarget[] {
  return [...WEB_POST_WP_TARGETS];
}

export const WEB_POST_STATUSES = ['draft', 'pending', 'published', 'rejected', 'hidden'] as const;
export type WebPostStatus = (typeof WEB_POST_STATUSES)[number];
export const WEB_POST_STATUS_LABELS: Record<WebPostStatus, string> = {
  draft: 'Nháp',
  pending: 'Chờ duyệt',
  published: 'Đã đăng',
  rejected: 'Bị trả lại',
  hidden: 'Đã gỡ khỏi web',
};

export const WEB_POST_MAX_IMAGES = 12;
export const WEB_POST_MAX_IMAGE_BYTES = 3 * 1024 * 1024;
export const WEB_POST_MAX_THUMB_BYTES = 512 * 1024;
export const WEB_POST_MAX_BODY = 20_000;
/** Đuôi chữ ký mặc định (GĐ đổi được ở Cài đặt của màn Đăng bài và Ảnh). */
export const WEB_POST_DEFAULT_SIGNATURE = 'Minh Tiến 0933160169';

/** "Nguyễn Văn A – Minh Tiến 0933160169" (anh Tiến: "Tên Nhân Viên + Minh Tiến 0933160169"). */
export function webPostSignature(fullName: string | null | undefined, suffix: string = WEB_POST_DEFAULT_SIGNATURE): string {
  const name = (fullName ?? '').trim().replace(/\s+/g, ' ');
  const tail = suffix.trim() || WEB_POST_DEFAULT_SIGNATURE;
  return name ? `${name} – ${tail}` : tail;
}

export interface WebPostWarning {
  code: 'phone' | 'money' | 'plate' | 'email' | 'tax_code';
  label: string;
  sample: string;
}

// Số công ty được phép nêu (hotline, chữ ký) – không cảnh báo
const COMPANY_PHONES = ['0933160169', '0937664114'];
const digits = (s: string) => s.replace(/\D/g, '');

/**
 * Soát chữ trước khi đăng công khai: SĐT (trừ hotline công ty), số tiền / đơn giá, biển số xe, email, MST.
 * Chỉ cảnh báo (NV sửa hoặc xác nhận); không thay được việc NV nhìn lại ảnh.
 */
export function webPostWarnings(text: string): WebPostWarning[] {
  const out: WebPostWarning[] = [];
  const t = text.normalize('NFC');
  const push = (code: WebPostWarning['code'], label: string, sample: string) => {
    if (!out.some((w) => w.code === code)) out.push({ code, label, sample: sample.trim().slice(0, 40) });
  };
  for (const m of t.matchAll(/(?:\+?84|0)(?:[\s.-]?\d){8,10}/g)) {
    const d = digits(m[0]).replace(/^84/, '0');
    if (!COMPANY_PHONES.includes(d)) push('phone', 'Có số điện thoại (không phải hotline công ty)', m[0]);
  }
  const money =
    /\d[\d.,\s]*\s?(?:đ\b|đồng|vnđ|vnd|₫|triệu|tr\b|nghìn|ngàn|k\/(?:kg|cây|m|tấn)|usd|\$)/i.exec(t) ?? /(?:giá|đơn giá|thành tiền)\s*:?\s*\d[\d.,]*/i.exec(t);
  if (money) push('money', 'Có số tiền / đơn giá', money[0]);
  const plate = /\b\d{2}[A-Z]{1,2}\d?[\s.-]?\d{3}[.\s]?\d{2}\b/.exec(t.toUpperCase());
  if (plate) push('plate', 'Có biển số xe', plate[0]);
  const email = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i.exec(t);
  if (email && !/@thepminhtien\.com$/i.test(email[0])) push('email', 'Có email', email[0]);
  const mst = /\b(?:MST|mã số thuế)\s*:?\s*\d{10}(?:-\d{3})?/i.exec(t);
  if (mst) push('tax_code', 'Có mã số thuế', mst[0]);
  return out;
}

/** Đường dẫn tạm cho bài: "giao-hang-ong-thep-ma-kem-dn50" (web có thể tự đặt lại). */
export function webPostSlug(title: string): string {
  return title
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
    .replace(/-+$/g, '');
}
